import { useState, useEffect } from "react";
import { fetchRollingData } from "../../../services/player-service";

export const STAT_OPTIONS = [
  { value: "woba", label: "wOBA", leagueAvg: 0.39 },
  { value: "avg", label: "AVG", leagueAvg: 0.27 },
  { value: "obp", label: "OBP", leagueAvg: 0.35 },
  { value: "slg", label: "SLG", leagueAvg: 0.42 },
];

export const useRollingChart = ({
  playerId,
  playerType = "batter",
  initialWindow = 25,
  minRequiredDataPoints = 10,
}) => {
  const [rollingData, setRollingData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [rollingWindow, setRollingWindow] = useState(initialWindow);
  const [lastPA, setLastPA] = useState("");
  const [currentValue, setCurrentValue] = useState(0);
  const [careerValue, setCareerValue] = useState(0);
  const [isDataSufficient, setIsDataSufficient] = useState(true);
  const [selectedStat, setSelectedStat] = useState("woba");

  const selectedStatOption = STAT_OPTIONS.find((s) => s.value === selectedStat) || STAT_OPTIONS[0];

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      if (!playerId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setRollingData([]);
      setLastPA("");
      setCurrentValue(0);
      setCareerValue(0);
      setIsDataSufficient(true);

      try {
        const response = await fetchRollingData(
          playerId,
          rollingWindow,
          playerType,
          selectedStat,
          controller.signal
        );
        if (controller.signal.aborted) return;

        const data = response.rolling_data || [];
        setIsDataSufficient(data.length >= minRequiredDataPoints);

        if (response.career_value !== undefined) {
          setCareerValue(response.career_value);
        } else if (data.length > 0) {
          const sumValue = data.reduce((sum, item) => sum + (item.raw_value || 0), 0);
          setCareerValue(sumValue / data.length);
        }

        setRollingData(data);

        if (data.length > 0) {
          const lastEntry = data[data.length - 1];
          setLastPA(lastEntry.game_date);
          setCurrentValue(lastEntry.rolling_value);
        }
      } catch (err) {
        if (err?.name === "AbortError") return;
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };

    fetchData();
    return () => controller.abort();
  }, [playerId, rollingWindow, playerType, minRequiredDataPoints, selectedStat]);

  return {
    rollingData,
    isLoading,
    rollingWindow,
    setRollingWindow,
    lastPA,
    currentValue,
    careerValue,
    isDataSufficient,
    selectedStat,
    setSelectedStat,
    selectedStatOption,
    leagueAvg: selectedStatOption.leagueAvg,
  };
};
